import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import { MaskLine, EASE } from "../motion";
import heroCollage from "../../assets/hero/finance-collage.webp";

export function Hero() {
  const { c, openApply } = useApp();
  const navigate = useNavigate();
  const h = c.hero;
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yPattern = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-[#001a40]"
    >
      <img
        src={heroCollage}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(0,26,64,0.98)_0%,rgba(0,26,64,0.9)_30%,rgba(0,26,64,0.42)_52%,rgba(0,26,64,0.06)_78%)]"
      />

      {/* subtle motion layer over the collage */}
      <motion.div style={{ y: yPattern }} className="pointer-events-none absolute inset-0">
        <div
          className="absolute -right-40 -top-40 h-[720px] w-[720px] rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(circle,#2f7d63 0%,transparent 65%)" }}
        />
        <div
          className="absolute -bottom-52 -left-24 h-[560px] w-[560px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle,#0f3227 0%,transparent 60%)" }}
        />
        <ContourLines />
      </motion.div>

      <motion.div
        style={{ y: yContent, opacity }}
        className="relative z-10 mx-auto w-full max-w-[1240px] px-5 pt-28 pb-20 md:px-8"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#e8a93d]/40 bg-[#e8a93d]/10 px-4 py-1.5"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#e8a93d]" />
          <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#e8a93d]">
            {h.badge}
          </span>
        </motion.div>

        <h1 className="font-display text-[13vw] font-extrabold leading-[0.92] tracking-tighter text-[#f5f7fa] sm:text-7xl lg:text-[6.4rem]">
          <MaskLine delay={0.25} className="font-light">{h.titleLines[0]}</MaskLine>
          <MaskLine delay={0.38}>{h.titleLines[1]}</MaskLine>
          <MaskLine delay={0.51} className="text-[#e8a93d]">{h.titleLines[2]}</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.75 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-[#f5f7fa]/75 md:text-lg"
        >
          {h.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-apply-btn"
            onClick={openApply}
            className="group inline-flex items-center gap-2 rounded-full bg-[#e8a93d] px-7 py-4 text-base font-bold text-[#002355] shadow-[0_16px_40px_-14px_rgba(232,169,61,0.8)] transition-transform duration-300 hover:scale-105"
          >
            {c.cta.apply}
            <ArrowUpRight size={20} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-products-btn"
            onClick={() => navigate("/products")}
            className="inline-flex items-center gap-2 rounded-full border border-[#f5f7fa]/25 px-7 py-4 text-base font-semibold text-[#f5f7fa] transition-colors duration-300 hover:bg-[#f5f7fa] hover:text-[#002355]"
          >
            {c.cta.products}
          </button>
          <button
            data-testid="hero-calculator-btn"
            onClick={() => navigate("/products#calculator")}
            className="inline-flex items-center gap-2 px-2 py-3 text-sm font-semibold text-[#f5f7fa]/75 transition-colors hover:text-[#e8a93d]"
          >
            {c.cta.calculator}
            <ArrowDown size={16} />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-[#f5f7fa]/12 pt-6"
        >
          {h.chips.map((chip) => (
            <div key={chip} className="flex items-center gap-2 text-sm text-[#f5f7fa]/85">
              <span className="h-1.5 w-1.5 rounded-full bg-[#e8a93d]" />
              {chip}
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => navigate("/products")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-[#f5f7fa]/60"
        aria-label="scroll down"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em]">{h.scroll}</span>
        <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
}

function ContourLines() {
  return (
    <svg
      className="absolute inset-0 h-full w-full opacity-[0.13]"
      preserveAspectRatio="xMidYMid slice"
      viewBox="0 0 1200 700"
      fill="none"
    >
      {Array.from({ length: 9 }).map((_, i) => (
        <path
          key={i}
          d={`M-50 ${120 + i * 62} C 250 ${60 + i * 62}, 450 ${200 + i * 62}, 700 ${140 + i * 62} S 1150 ${60 + i * 62}, 1300 ${150 + i * 62}`}
          stroke="#f5f7fa"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
